import React, { useState } from 'react';

import { useFormik } from 'formik';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { confirmResetPassword } from 'api/endpoints/auth';
import Button from 'components/Button';
import ErrorMessage from 'components/ErrorMessage';
import Input from 'components/Input';
import TitleInput from 'components/TitleInput';
import { ENUM_PASSWORD_CREATE_ERROR } from 'constants/enum';
import { toastError, toastSuccess } from 'lib/toast';
import AppRoutes from 'routes/routes';
import { checkPasswordRegister } from 'utils/helper';

const EnterNewPassword = ({ token }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isSubmitted, setIsSubmitted] = useState(false);

  const initialValues = {
    password: '',
    passwordConfirm: '',
  };

  const form = useFormik({
    initialValues: initialValues,
    validateOnChange: isSubmitted,
    validateOnBlur: false,
    validate: (values) => {
      const errors = {};
      if (!values.password || checkPasswordRegister(values.password).length > 0) errors.password = checkPasswordRegister(values.password);
      if (!values.passwordConfirm || values.password !== values.passwordConfirm) errors.passwordConfirm = true;
      setIsSubmitted(true);
      return errors;
    },
    onSubmit: async (values) => {
      try {
        await confirmResetPassword({ token, password: values.password })
          .then((response) => {
            toastSuccess(response.message);
            navigate(AppRoutes.login);
          })
          .catch((errors) => {
            toastError(errors?.message);
          });
      } catch {
        // Handle error
      }
    },
  });

  return (
    <>
      <fieldset>
        <TitleInput text={t('NEW_PASSWORD')} required={true} />
        <Input
          id="password"
          type="password"
          name="password"
          value={form.values.password}
          onChange={form.handleChange}
          invalid={form.errors?.password}
        />
        <div className={`text-sm ${form.errors?.password ? 'text-danger-229' : 'text-secondary-354'}`}>
          {!form.values.password && form.errors.password ? (
            <ErrorMessage>{t('NOT_EMPTY')}</ErrorMessage>
          ) : (
            <>
              {form.errors.password && (
                <ErrorMessage>
                  <div>
                    <p>{t('PASSWORD_INVALID_TEXT')}</p>
                    <p>{t('PASSWORD_INVALID_REQUIRED_TEXT')}</p>
                  </div>
                </ErrorMessage>
              )}
              {!form.errors.password && <p>{t('PASSWORD_REQUIRED_TEXT')}</p>}
              <ul className={`list-disc ${form.errors?.password ? 'ml-11' : 'ml-6'}`}>
                {(!form.errors.password || (form.errors.password && form.errors.password.includes(ENUM_PASSWORD_CREATE_ERROR.LENGTH))) && (
                  <li>{t('PASSWORD_REQUIRED_LENGTH')}</li>
                )}
                {(!form.errors.password ||
                  (form.errors.password &&
                    _.intersection(form.errors.password || [], [
                      ENUM_PASSWORD_CREATE_ERROR.HAS_LOWERCASE,
                      ENUM_PASSWORD_CREATE_ERROR.HAS_UPPERCASE,
                      ENUM_PASSWORD_CREATE_ERROR.HAS_NUMBER,
                    ]).length > 0)) && <li>{t('PASSWORD_REQUIRED')}</li>}
                {(!form.errors.password || (form.errors.password && form.errors.password.includes(ENUM_PASSWORD_CREATE_ERROR.HAS_FULL_WIDTH))) && (
                  <li>{t('PASSWORD_REQUIRED_CHARACTER')}</li>
                )}
                {(!form.errors.password ||
                  (form.errors.password &&
                    _.intersection(form.errors.password || [], [
                      ENUM_PASSWORD_CREATE_ERROR.HAS_SYMBOLS,
                      ENUM_PASSWORD_CREATE_ERROR.HAS_SYMBOLS_REQUIRED,
                    ]).length > 0)) && <li>{t('PASSWORD_REQUIRED_SYMBOL')}</li>}
              </ul>
            </>
          )}
        </div>
      </fieldset>

      <fieldset>
        <TitleInput text={t('NEW_PASSWORD_CONFIRM')} required={true} />
        <Input
          id="password-confirm"
          type="password"
          name="passwordConfirm"
          value={form.values.passwordConfirm}
          onChange={form.handleChange}
          invalid={form.errors?.passwordConfirm}
        />
        <p className={`text-sm ${form.errors.passwordConfirm ? 'text-danger-229' : 'text-secondary-354'}`}>
          {!form.values.passwordConfirm && form.errors.passwordConfirm && <ErrorMessage>{t('NOT_EMPTY')}</ErrorMessage>}
          {form.values.passwordConfirm && form.errors.passwordConfirm && <ErrorMessage>{t('PASSWORD_MATCHING')}</ErrorMessage>}
        </p>
      </fieldset>

      <Button
        onClick={() => {
          form.handleSubmit();
        }}
        className={'w-60 h-9.5 mt-8 mx-auto'}
        color={'primary'}
        type="button"
        text={t('SUBMIT_NEW_PASSWORD')}
      />
    </>
  );
};

export default React.memo(EnterNewPassword);
